import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  User, Mail, Phone, MapPin, Loader2, Save, Camera, Users, KeyRound
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentProfilePage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  const photoInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    occupation: '',
    profile_photo_url: '',
    children: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/parents/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || ''
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/parents/my-profile`, {
        phone: profile.phone,
        address: profile.address,
        occupation: profile.occupation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Profile Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/my-parent-profile-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setProfile(prev => ({ ...prev, profile_photo_url: response.data.photo_url }));
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Profile">
      <div className="space-y-6">
        {/* Profile Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-orange-500 to-amber-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-6">
                {/* Profile Photo */}
                <div className="relative group">
                  <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                    {profile.profile_photo_url ? (
                      <img 
                        src={`${API_URL}${profile.profile_photo_url}`} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-12 h-12 text-white" />
                    )}
                  </div>
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6" />
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                
                <div>
                  <h2 className="text-2xl font-bold">{profile.name || user?.name || 'Parent'}</h2>
                  <p className="text-orange-100">{profile.occupation || 'Parent/Guardian'}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      <Users className="h-3 w-3 mr-1" /> Parent
                    </Badge>
                    {profile.children?.length > 0 && (
                      <Badge variant="secondary" className="bg-white/20 text-white">
                        {profile.children.length} Child{profile.children.length > 1 ? 'ren' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="ml-auto">
                  <Button 
                    variant="secondary" 
                    className="bg-white/20 hover:bg-white/30 text-white"
                    onClick={() => setChangePasswordOpen(true)}
                    data-testid="change-password-btn"
                  >
                    <KeyRound className="h-5 w-5 mr-2" /> Change Password
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <User className="h-4 w-4" /> Full Name
                  </Label>
                  <Input value={profile.name || ''} disabled className="bg-muted" />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email
                  </Label>
                  <Input value={profile.email || ''} disabled className="bg-muted" />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Phone Number
                  </Label>
                  <Input 
                    value={profile.phone || ''} 
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <Label>Occupation</Label>
                  <Input 
                    value={profile.occupation || ''} 
                    onChange={(e) => setProfile(prev => ({ ...prev, occupation: e.target.value }))}
                    placeholder="Enter occupation"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Address
                  </Label>
                  <Input 
                    value={profile.address || ''} 
                    onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Children Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Children</CardTitle>
              </CardHeader>
              <CardContent>
                {profile.children?.length > 0 ? (
                  <div className="space-y-4">
                    {profile.children.map((child, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center">
                          <User className="h-6 w-6 text-pink-600" />
                        </div>
                        <div>
                          <p className="font-medium">{child.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {child.class_name} {child.section && `- ${child.section}`}
                          </p>
                          {child.roll_number && (
                            <p className="text-xs text-muted-foreground">Roll: {child.roll_number}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No children linked to your account</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        {/* Change Password Dialog */}
        <ChangePasswordDialog 
          open={changePasswordOpen} 
          onOpenChange={setChangePasswordOpen}
          getAuthHeader={getAuthHeader}
        />
      </div>
    </DashboardLayout>
  );
};

export default ParentProfilePage;
